﻿export interface Event {
	// Base Event Information
	EventId: number;
	Start: Date;
	End: Date | null;
	EventTypeCode: string;
	EventTypeName: string;
	CreatedAt: Date;
	CreatedBy: string;
	UpdatedAt: Date | null;
	UpdatedBy: string | null;
	EventComments: string | null;
	IsMatched: boolean;
	IsDraft: boolean;

	// Production Event Information
	SectionId: number | null;
	SectionName: string | null;
	TonsPerHour: number | null;
	SectionMillYield: number | null;
	SectionFinishedYield: number | null;
	SectionTargetTPH: number | null;
	BilletSize: number | null;
	Description: string | null;
	StatusCode: string | null;
	StatusName: string | null;
	BilletsStatusCode?: string | null;
	BilletsStatusName?: string | null;
	InquiryStatusCode: string | null;
	InquiryStatusName: string | null;
	InquiryCloseDate: Date | null;
	Deadline: Date | null;

	TargetTPH: number | null;

	// Scheduled Values
	ScheduledChargedTons: number | null;
	ScheduledRolledTons: number | null;
	ScheduledFinishedTons: number | null;
	BudgetTPH: number | null;
	ScheduledRollingHours: number | null;
	ScheduledRollChangeHours: number | null;
	ScheduledMillYield: number | null;
	ScheduledFinishedYield: number | null;

	// Actual Values — month-specific (filtered by @QueryMonthStart)
	ActualChargedTons: number | null;
	ActualRolledTons: number | null;
	ActualFinishedTons: number | null;
	ActualTPH: number | null;
	ActualRollingHours: number | null;
	ActualRollChangeHours: number | null;
	ActualMillYield: number | null;
	ActualFinishedYield: number | null;

	StackerFinishedTons: number | null;

	// Actual totals — aggregated across all months (for multi-month display)
	TotalActualChargedTons: number | null;
	TotalActualRolledTons: number | null;
	TotalActualFinishedTons: number | null;
	TotalActualRollingHours: number | null;

	// Calculated Fields
	DurationHours: number | null;
	ChargeVariance: number | null;
	RolledVariance: number | null;

	PreviousSection: string | null;
	PreviousEnd: Date | null;
	NextSection: string | null;
	NextStart: Date | null;
}

export const EVENT_INITIAL_VALUES: Event = {
	EventId: 0,
	Start: new Date(),
	End: null,
	EventTypeCode: "",
	EventTypeName: "",
	CreatedAt: new Date(),
	CreatedBy: "",
	UpdatedAt: null,
	UpdatedBy: null,
	EventComments: null,
	IsMatched: false,
	IsDraft: false,
	SectionId: null,
	SectionName: null,
	TonsPerHour: null,
	SectionMillYield: null,
	SectionFinishedYield: null,
	SectionTargetTPH: null,
	BilletSize: null,
	Description: null,
	StatusCode: "P",
	StatusName: "Planned",
	BilletsStatusCode: "H",
	BilletsStatusName: null,
	InquiryStatusCode: "C",
	InquiryStatusName: null,
	InquiryCloseDate: null,
	Deadline: null,
	TargetTPH: null,
	ScheduledChargedTons: null,
	ScheduledRolledTons: null,
	ScheduledFinishedTons: null,
	BudgetTPH: null,
	ScheduledRollingHours: null,
	ScheduledRollChangeHours: null,
	ScheduledMillYield: null,
	ScheduledFinishedYield: null,
	ActualChargedTons: null,
	ActualRolledTons: null,
	ActualFinishedTons: null,
	ActualTPH: null,
	ActualRollingHours: null,
	ActualRollChangeHours: null,
	ActualMillYield: null,
	ActualFinishedYield: null,
	StackerFinishedTons: null,
	TotalActualChargedTons: null,
	TotalActualRolledTons: null,
	TotalActualFinishedTons: null,
	TotalActualRollingHours: null,
	DurationHours: null,
	ChargeVariance: null,
	RolledVariance: null,

	PreviousSection: null,
	PreviousEnd: null,
	NextSection: null,
	NextStart: null,
};

export interface ProductionEventActual {
	ProductionEventActualId: number;
	MonthStart: Date;
	ActualChargedTons: number | null;
	ActualRolledTons: number | null;
	ActualFinishedTons: number | null;
	ActualTPH: number | null;
	ActualRollingHours: number | null;
	ActualRollChangeHours: number | null;
	ActualMillYield: number | null;
	ActualFinishedYield: number | null;
}

export interface EventSegment {
	event: Event;
	dayIndex: number;
	weekIndex: number;
	isStart: boolean;
	isEnd: boolean;
	segStart: Date;
	segEnd: Date;
	originalStart: Date;
	originalEnd: Date;
	spanDays?: number;
	firstDayInWeek?: number;
	lastDayInWeek?: number;
}

// Event with comparison fields for Draft vs Main comparison
export interface EventToCompare extends Event {
	GroupId: number | null;
	ActionTypeCode: "C" | "U" | "D" | "M" | null; // C=Created, U=Updated, D=Deleted, null=NoChange
	Duration: number;
	ChangedFields: String | null;
	PositionDelta: number;
}

export interface EventsToCompareResult {
	mainEvents: EventToCompare[];
	draftEvents: EventToCompare[];
}

// Draft Snapshot for restoration
export interface DraftSnapshot {
	SnapshotId: number;
	CreatedAt: Date;
	CreatedBy: string;
	Reason: "RESET" | "MANUAL" | "RESTORE";
	Description: string | null;
	EventCount: number;
	ProductionEventCount: number;
	FirstEventStart: Date | null;
	LastEventEnd: Date | null;
}

export interface RestoreSnapshotResult {
	RestoredFromSnapshotId: number;
	BackupSnapshotId: number;
	EventsDeleted: number;
	EventsInserted: number;
	ProductionEventsDeleted: number;
	ProductionEventsInserted: number;
}

export interface CreateSnapshotResult {
	SnapshotId: number | null;
	EventCount: number;
	ProductionEventCount: number;
	DeletedOldSnapshots: number;
	Skipped: boolean;
}
